import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useIsAuthenticated, useMsal } from "@azure/msal-react";

import DropDownModal from "@views/Cancellations/DropdownModal";
import ChargeCard from '@views/CancellationsV2/ChargeCard';
import { ErrorBox, InfoBox, WarningBox } from "@components/cards/messageBox";
import { NavBar } from '@components/navigation/NavBar/NavBar';
import Button, { ButtonStyle } from '@components/buttons/Button/Button';
import useServiceHook from "@hooks/useServiceHook";
import SubscriptionService, {
    CancellationChargesV2Response,
    SpecificFeeTypes,
    AncillaryFee, LongFeeNames,
} from "@services/subscription/SubscriptionService";
import LoadingSpinner from "@utils/loadingSpinner";
import { Country } from '@utils/constants/localisation';
import {
    convertMajorToMinorCurrency,
    convertMinorToMajorCurrency,
    countrySpecificCurrency,
    dateToISOFormat
} from '@utils/misc/functions';

import { SelectedSubscriptionState } from './selectedSubscriptionReducer';
import { CancellationChargesState, CancellationChargesAction } from './cancellationChargesReducer';
import {CancellationReasonState} from './cancellationReasonReducer';
import { CancellationVehicleState } from './cancellationVehicleReducer';

type Props = {
    navigateTarget: string;
    selectedSubscriptionState: SelectedSubscriptionState;
    cancellationVehicleState: CancellationVehicleState;
    cancellationChargesState: CancellationChargesState;
    cancellationChargesDispatch: React.Dispatch<CancellationChargesAction>;
    cancellationReasonState: CancellationReasonState
    country: Country;
};

export default function CancellationCharges({
    navigateTarget,
    selectedSubscriptionState,
    cancellationVehicleState,
    cancellationChargesState,
    cancellationChargesDispatch,
    cancellationReasonState,
    country,
}: Props) {
    const navigate = useNavigate();
    const msalContext = useMsal();
    const isAuthenticated = useIsAuthenticated();

    // Narrowed subscription id for type safety
    const subsId = selectedSubscriptionState.selectedSubsId;

    // Local state for cancellation charges so we can handle 400 responses directly
    const [chargesLoading, setChargesLoading] = useState<boolean>(false);
    const [chargesError, setChargesError] = useState<boolean>(false);
    const [chargesErrMsg, setChargesErrMsg] = useState<string | null>(null);
    const [chargesDetails, setChargesDetails] = useState<CancellationChargesV2Response | undefined>(undefined);

    // Fetch calculated charges (direct call) so we can inspect 4xx error responses
    useEffect(() => {
        if (!subsId) {
            // nothing to do until we have a subscription id
            return;
        }

        let cancelled = false;
        const defaultErrorMessage = 'Failed to fetch cancellation charges';

        const fetchCharges = async () => {
            setChargesLoading(true);
            setChargesError(false);
            setChargesErrMsg(null);
            try {
                const noticeGiven = selectedSubscriptionState.noticeGiven;
                const dateSubmittedNotice = (!noticeGiven || noticeGiven === "N/A")
                    ? dateToISOFormat(new Date())
                    : noticeGiven;

                const resp = await SubscriptionService.getCancellationCharges_v2(
                    { 
                        country,
                        contract_end_date: cancellationVehicleState.cancellationDate as `${string}-${string}-${string}`,
                        date_submitted_notice: dateSubmittedNotice as `${string}-${string}-${string}`,
                        cancellation_type: cancellationReasonState.cancellationType,
                        cancellation_category: cancellationReasonState.cancellationCategory,
                        is_vehicle_remarketable: cancellationReasonState.vehicleRemarketable,
                    },
                    subsId,
                    { msalContext, isAuthenticated }
                );

                if (!cancelled) {
                    setChargesDetails(resp);
                    setChargesLoading(false);
                    setChargesError(false);
                }
                console.debug("Cancellation charges fetched:", {
                    country,
                    subs_id: selectedSubscriptionState.selectedSubsId,
                    data: resp
                });
            } catch (err: any) {
                if (cancelled) return;
                // Handle axios-like 4xx responses
                if (err?.response?.status >= 400 && err?.response?.status < 500) {
                    setChargesErrMsg(err.response.data?.error?.message || `Unexpected API Error: (${err.response.status}). Cannot proceed.`);
                } else {
                    setChargesErrMsg(defaultErrorMessage);
                }
                setChargesError(true);
                setChargesLoading(false);
            }
        };

        fetchCharges();

        return () => {
            cancelled = true;
        };
    }, [subsId]);


    const response = chargesDetails as CancellationChargesV2Response | undefined;
    const charges: SpecificFeeTypes[] = response?.charges || [];
    const warnings: string[] = response?.warnings || [];
    // Write calculated charges  API payload into reducer when available
    useEffect(() => {
        if (!chargesLoading && !chargesError && response && Array.isArray(response.charges)) {
            cancellationChargesDispatch({ type: 'SET_API_CHARGES', payload: response });
        }
    }, [chargesLoading, chargesError, response, cancellationChargesDispatch]);


    // Fetch available addons to use as options for ancillary charges
    const [addonsLoading, _addonsError, _addonsErrMsg, addonsData] = useServiceHook(
        SubscriptionService.getAddons,
        [{ country: country, accounting_prefix: ["405", "406", "2307"].join(',') }, { msalContext, isAuthenticated }],
        true,
        []
    );
    // Convert addon prices from major currency (dollars) to minor currency (cents) for storage
    const addonsDetails = Array.isArray(addonsData)
        ? addonsData.map(addon => ({
            ...addon,
            price: convertMajorToMinorCurrency(addon.price)
        }))
        : [];

    // Objects for storing the selected ancillary charges
    const [isAncillaryModalOpen, setisAncillaryModalOpen] = useState<boolean>(false);

    // Handler for when an ancillary charge is selected from the modal
    const handleSelectedAncillary = (selectedOptions: any) => {
        // DropdownModal returns: { item, quantity, unit, total }
        // Look up the addon to get the Chargebee ID
        const addon = addonsDetails.find((a: any) => a.name === selectedOptions.item);

        if (!addon) {
            console.error('Addon not found in addonsDetails:', selectedOptions.item);
            return;
        }

        // Convert selected unit price from major (displayed in modal) to minor for storage
        const unitPriceMinor = convertMajorToMinorCurrency(Number(selectedOptions.unit) || 0);
        const quantity = selectedOptions.quantity || 1;
        const existing = cancellationChargesState?.ancillary_charges || [];

        // Create AncillaryFee with correct Chargebee addon ID
        const newAnc: AncillaryFee = {
            fee: unitPriceMinor*quantity,
            cb_addon_id: addon.id,  // Actual Chargebee addon ID (not the name!)
            quantity: quantity,
            cb_addon_name: addon.name,
            long_fee_name: LongFeeNames.AncillaryFee,
            unit_price: unitPriceMinor,
        } as AncillaryFee;

        const updated = [...existing, newAnc];
        cancellationChargesDispatch({ type: 'SET_ANCILLARY_CHARGES', payload: updated });
        setisAncillaryModalOpen(false);
    };

    const handleRemoveAncillary = (index: number) => {
        const existing = cancellationChargesState?.ancillary_charges || [];
        const updated = existing.filter((_, i) => i !== index);
        cancellationChargesDispatch({ type: 'SET_ANCILLARY_CHARGES', payload: updated });
    };

    const handleNext = () => {
        // Navigation to the review screen
        navigate(navigateTarget);
    };

    const sumCharges = () => {
        const apiChargesTotal = charges.reduce((sum, charge) => sum + charge.fee, 0);
        const ancillaryChargesTotal = (cancellationChargesState.ancillary_charges).reduce((sum, charge) => sum + charge.fee, 0);
        return apiChargesTotal + ancillaryChargesTotal;
    }
    const currency = countrySpecificCurrency(country);

    return (
        <>
            <NavBar pageTitle="Charges" />
            <div style={{ margin: '2.5vh 2.5vw' }}>
                <p>Step 5</p>

                {/*Ancillary Charges*/}
                {addonsLoading
                    ? <LoadingSpinner />
                    : <>
                        <p>Please add any <strong>extra ancillary charges (e.g. damage).</strong></p>
                        <div>
                            {/* Display selected ancillary charges from reducer */}
                            <div style={{ marginTop: '2rem' }}>
                                <strong>Selected ancillary charges</strong>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 8 }}>
                                    {(cancellationChargesState?.ancillary_charges || []).map((a, idx) => (
                                        <div key={`anc-${idx}`}
                                            style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                                            <div style={{ flex: 1 }}><ChargeCard charge={a} country={country} /></div>
                                            <div>
                                                <Button label="Remove" onClickFunc={() => handleRemoveAncillary(idx)}
                                                    btnType={ButtonStyle.secondary} />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div style={{ margin: '2.5vh 0vw' }}>
                                <Button
                                    label="Add ancillary charge"
                                    onClickFunc={() => setisAncillaryModalOpen(true)}
                                    btnType={ButtonStyle.secondary}
                                    testId='open-modal-button'
                                />
                            </div>

                            {isAncillaryModalOpen && (
                                // Uses the 1.0 modal to select addons for ancillary charges
                                <DropDownModal
                                    onClose={() => setisAncillaryModalOpen(false)}
                                    // Pass options with price converted back to major currency for display
                                    options={addonsDetails.map(addon => ({ ...addon, price: convertMinorToMajorCurrency(addon.price) }))}
                                    onSelection={(selectedOptions: any) => handleSelectedAncillary(selectedOptions)}
                                    country={country}
                                />
                            )}


                        </div>
                    </>
                }


                {/*Calculated charges*/}
                {chargesLoading && <InfoBox message="Fetching cancellation charges..." />}
                {/*/TODO loading spinner*/}
                {chargesError && <ErrorBox message={chargesErrMsg || 'Failed to load charges'} />}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem 0', marginTop: '1rem' }}>
                    {charges.length === 0 && !chargesLoading && (
                        <div>No charges returned from the API.</div>
                    )}

                    {charges.map((c, idx) => (
                        <ChargeCard key={idx} charge={c} country={country} />
                    ))}
                </div>
                {warnings.length > 0 && (
                    <div style={{ marginBottom: 16 }}>
                        <WarningBox message={warnings.join('\n')} />
                    </div>
                )}

                {/* Total charges section */}
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <b>Total cancellation charges:</b>
                    <div style={{ fontWeight: 700, fontSize: '1.1em', minWidth: 220, textAlign: 'right' }}>
                         {currency}{convertMinorToMajorCurrency(sumCharges())}
                    </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '2.5vh 0', marginTop: '2.5vh' }}>
                    <Button
                        label="Next"
                        onClickFunc={handleNext}
                    />
                </div>
            </div>
        </>
    );
};
